import { Component, computed, DestroyRef, inject, input, LOCALE_ID, OnInit, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OrderItem } from '../../../../pages/admin-dashboard/orders-management/orders.model';
import {
  ORDER_STATUS_ID,
  ORDER_STATUS_TITLES,
  ORDER_STATUS_ID_TO_KEY,
  ORDER_STATUS_DEFAULT_COLOR_CLASS,
  ORDER_STATUS_ID_TO_COLOR_CLASS,
} from '../../../../pages/admin-dashboard/orders-management/order-status.constants';
import { ViewOrderModel } from '../../../../pages/admin-dashboard/orders-management/view-order/view-order.model';
import { ActivatedRoute } from '@angular/router';
import { OrderService } from '../../../../services/orders/order.service';
import { finalize, forkJoin } from 'rxjs';
import { Loading } from '../../../../utils/loading/loading';
import { ProductsFileMgt } from '../../../../services/product/file-management/products.file-mgt.service';
import { SafeUrl } from '@angular/platform-browser';
import { ErrorData } from '../../../../utils/error-data/error-data';
import { OrderActionPopup } from '../../../../shared/ui/order-action-popup/order-action-popup';
import { ItemStatusPopup } from './actions/item-status-popup/item-status-popup';
import { OrderProductItem } from './utils/order-product-item/order-product-item';
import { OrderSummaryCard } from './utils/order-summary-card/order-summary-card';
import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';
import { ReasonsService } from '../../../../services/reasons/reasons.service';
import { ActionsService } from '../../../../services/actions/actions.service';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { hydratePackageItems } from '../../../../store/order/package-order/package-item.actions';
import { OrderPackageService } from '../../../../services/orders/admin-order/order-package.service';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { OrderOfferItem } from './utils/order-offer-item/order-offer-item';
import { OrderBundleItem } from './utils/order-bundle-item/order-bundle-item';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../services/scope/scope-constants';
import { resetPackageActions } from '../../../../store/order/confirm-package-order/confirm-package.actions';
import {
  ConfirmPackageItemDto,
  ConfirmPackageRequestDto,
  ProductShortageData,
  ConvertShortageData,
} from './actions/confirm-package.model';
import { DropFromOrderConfirm } from './utils/shortage/drop-from-order-confirm/drop-from-order-confirm';
import { ConfirmProductPopup } from './utils/shortage/confirm-product-popup/confirm-product-popup';
import { ConvertToProductsPopup } from './utils/shortage/convert-to-products-popup/convert-to-products-popup';
import { DropAllConfirm } from './utils/shortage/drop-all-confirm/drop-all-confirm';
import { CollectCashPopup } from './actions/collect-cash-popup/collect-cash-popup';
import { CancelOrderRequest } from '../../../../pages/admin-dashboard/orders-management/orders.model';
import { CancellationReason } from '../../../../pages/admin-dashboard/orders-management/view-order/view-order.model';
import { UndeliverPopup } from './actions/undelivered-popup/undeliver-popup/undeliver-popup';
import { AuthService } from '../../../../services/auth-service';
import isRowProcessed from './utils/helpers/isRowProcessed';
import { RejectHighlight } from '../../../../shared/ui/icons/reject-highlight/reject-highlight';

@Component({
  selector: 'app-view-order-details',
  standalone: true,
  imports: [
    CommonModule,
    Loading,
    ErrorData,
    OrderActionPopup,
    ItemStatusPopup,
    OrderProductItem,
    OrderSummaryCard,
    SubmitButton,
    ConfirmationDialog,
    OrderOfferItem,
    OrderBundleItem,
    SuccessfulNotification,
    HasScopeDirective,
    DropFromOrderConfirm,
    ConfirmProductPopup,
    ConvertToProductsPopup,
    DropAllConfirm,
    CollectCashPopup,
    UndeliverPopup,
    RejectHighlight
],
  templateUrl: './view-order-details.html',
  styleUrl: './view-order-details.scss',
})
export class ViewOrderDetails implements OnInit {
  isCancelPopup = signal(false);
  cancelling = signal(false);
  cancellationReasons = signal<CancellationReason[]>([]);
  otherCancellationReasonId = computed(() => {
    const reason = this.cancellationReasons().find(
      (reason) => reason.titleEn?.trim().toLowerCase() === 'other' || reason.titleAr?.trim() === 'أخرى',
    );

    return reason?.id ?? null;
  });
  cancelError = signal('');
  cancelSuccess = signal(false);
  private actionsServie = inject(ActionsService);
  private route = inject(ActivatedRoute);
  private orderService = inject(OrderService);
  private destroyRef = inject(DestroyRef);
  private locale = inject(LOCALE_ID);
  private fileMgt = inject(ProductsFileMgt);
  private reasonsService = inject(ReasonsService);
  private activateRoute = inject(ActivatedRoute);
  private store = inject(Store);
  private packageService = inject(OrderPackageService);
  private authService = inject(AuthService);
  readonly SCOPES = SCOPES;
  role = input<'admin' | 'pharmacist'>('admin');
  undeliveredReasons = signal<any>('');

  successNotification = signal(false);
  successMessage = signal('');

  packageState = toSignal(
    this.store.select((state: any) => state['packageItems'] || {}),
    { initialValue: {} },
  );

  confirmPackageState = toSignal(
    this.store.select((state: any) => state['confirmPackage'] || {}),
    { initialValue: {} },
  );

  getConfirmActionFor(itemId: string) {
    const state = this.confirmPackageState();
    return state[itemId]?.action || null;
  }

  convertedProductsFor(itemId: string) {
    const state = this.confirmPackageState();
    const entry = state[itemId];
    if (!entry || entry.action !== 'CONVERT' || !entry.products?.length) return [];

    const item = this.orderData()?.items?.find((i) => +i.id === +itemId) as any;
    if (!item) return [];

    const source = item.bxGyOffer ?? item.offer ?? item.bundle;
    const innerRows = (item.products ?? source?.products ?? []) as any[];

    return entry.products
      .map((converted: any) => {
        const row = innerRows.find((r: any) => +r.product?.id === +converted.customerProductId);
        return row?.product ? { product: row.product, quantity: converted.quantity } : null;
      })
      .filter((c: any): c is { product: any; quantity: number } => !!c);
  }

  visibleItems = computed<any[]>(() => {
    const data = this.orderData();
    if (!data?.items?.length) return [];
    const state = this.confirmPackageState();
    return data.items.filter((item) => state[item.id]?.action !== 'DROP');
  });

  showOnlyPackagedFor(itemId: string) {
    return this.getConfirmActionFor(itemId) === 'CONFIRM';
  }

  shortageItemIds = computed(() => {
    const data = this.orderData();
    if (!data?.items?.length) return [];
    return data.items.filter((item) => this.packagingOfItem(item).notPackagedRows.length > 0).map((item) => item.id);
  });

  allShortagesHandled = computed(() => {
    const ids = this.shortageItemIds();
    const state = this.confirmPackageState();
    return ids.every((id) => !!state[id]?.action);
  });

  allItemsProcessed = computed(() => {
    const data = this.orderData();
    if (!data?.items?.length) return false;

    const state = this.packageState();

    const isRowProcessed = (
      entry: { packageQuantity: string | null; notPackageRows: any[] } | undefined,
      ordered: any,
    ): boolean => {
      if (!entry) return false;
      const packed = entry.packageQuantity != null ? +(entry.packageQuantity || 0) : 0;
      const notPacked = (entry.notPackageRows ?? []).reduce((acc: number, r: any) => acc + (+r.quantity || 0), 0);
      return packed + notPacked >= +ordered;
    };

    return data.items.every((item) => {
      const offer = (item as any).offer ?? (item as any).bxGyOffer;
      const bundle = (item as any).bundle;
      if (offer || bundle) {
        const products = (item as any).products ?? offer?.products ?? bundle?.products ?? [];
        if (products.length === 0) return false;
        return products.every((p: any) => {
          const entry =
            state[`${item.id}_${p.id}`] ?? state[`${item.id}_${p.product?.id}`] ?? state[p.product?.id] ?? state[p.id];
          return isRowProcessed(entry, p.quantity);
        });
      }
      return isRowProcessed(state[item.id], item.quantity);
    });
  });

  isOrderPartiallyPackaged = computed(() => {
    const data = this.orderData();
    if (!data?.items) return false;
    return data.items.some((item) => this.packagingOfItem(item).notPackagedRows.length > 0);
  });

  canHandleShortages = computed(() => this.status() === 'partially_packaged');

  orderId = computed(() => this.activateRoute.snapshot.paramMap.get('id'));
  isArabic = computed(() => this.locale.startsWith('ar'));
  imagePreviews = signal<Record<string, string | SafeUrl>>({});
  isPrintPopup = signal(false);
  openPackage = signal(false);
  reviewalApprovedPopup = signal(false);
  rejectReview = signal(false);
  openDeliver = signal(false);
  openRepackage = signal(false);
  openRepackagePopup = signal(false);
  optionData = signal([]);
  printing = signal(false);
  errPrintAction = signal('');
  repackaging = signal(false);
  errRepackageAction = signal('');
  isOrderPrinted = computed(() => this.orderData()?.status?.titleEn === 'Printed');
  packageClicked = output<void>();

  selectedPackageItem = signal<{
    id: string;
    titleEn: string;
    titleAr: string;
    quantity: string;
    type: 'product' | 'offer' | 'bundle';
    storeKey?: string;
  } | null>(null);

  resettingPackage = signal(false);
  errResetAction = signal('');
  resetPackagePopup = signal(false);
  submitingPackage = signal(false);
  errSubmitAction = signal('');
  submitPackagePopup = signal(false);
  isShipPopup = signal(false);
  errorShip = signal('');

  confirmingPackage = signal(false);
  errConfirmAction = signal('');
  confirmPackagePopup = signal(false);
  receiveReturnedProductsPopup = signal(false);

  isConfirmProductPopupOpen = signal(false);
  isDropOrderPopupOpen = signal(false);
  selectedItemData = signal<ProductShortageData | null>(null);
  selectedItemId = signal<string>('');
  selectedItemType = signal<'product' | 'bundle'>('product');
  isConvertProductsPopupOpen = signal(false);
  isDropAllOfferPopupOpen = signal(false);
  selectedOfferData = signal<ConvertShortageData | null>(null);
  selectedOfferItemId = signal<string>('');
  selectedOfferItemType = signal<'offer' | 'bundle'>('offer');

  orderData = signal<ViewOrderModel | null>(null);
  onAction = output<{ title: string; data: OrderItem }>();
  loading = signal(false);
  error = signal('');
  imagePreview = signal('');
  shipPopup = signal(false);
  shipping = signal(false);

  isDeliverPopup = signal(false);
  isUndeliverPopup = signal(false);
  isPartiallyDeliverPopup = signal(false);
  delivering = signal(false);
  undelivering = signal(false);
  partiallyDelivering = signal(false);
  errDeliver = signal('');
  errUndeliver = signal('');
  errPartiallyDeliver = signal('');

  receiveReturnedProductsView = signal(false);
  receivingReturnedProducts = signal(false);
  errReceivingReturnedProducts = signal('');
  deliverAmountCollected = signal<number | null>(null);

  isCollectCashPopup = signal(false);
  collectingCash = signal(false);
  errCollectCash = signal('');

  status = computed(() => {
    const id = this.orderData()?.status?.id;
    return id != null ? (ORDER_STATUS_ID_TO_KEY[Number(id)] ?? 'unknown') : 'pending';
  });

  rejectionReason = computed(() => {
    if (this.status().toLowerCase() !== 'rejected') return null;

    const orderReason = this.orderData()?.orderReason;
    if (!orderReason) return null;

    const title = this.isArabic() ? orderReason.reason?.titleAr : orderReason.reason?.titleEn;

    return orderReason.otherReason?.trim() || title || null;
  });

  expectedAmount = computed(() => {
    const value = Number(this.orderData()?.totalBusinessPrice ?? 0);
    return Math.round(value * 100) / 100;
  });

  isCashSegment = computed(() => {
    const order = this.orderData() as any;
    const segment =
      order?.pharmacy?.segment?.titleEn ||
      order?.pharmacy?.segment ||
      order?.segment?.titleEn ||
      order?.segment ||
      'cash';
    return String(segment).toLowerCase().includes('cash');
  });

  canShowCollectCash = computed(() => {
    const currentStatus = this.status().toLowerCase();
    const eligible =
      currentStatus === 'delivered' ||
      currentStatus === 'partially-delivered' ||
      currentStatus === 'partially_delivered';
    const alreadyCollected = currentStatus === 'cash-collected';
    return eligible && this.isCashSegment() && !alreadyCollected;
  });

  statusContent = computed(() => {
    const serverStatus = this.orderData()?.status;
    const title = this.isArabic() ? serverStatus?.titleAr : serverStatus?.titleEn;
    return title || (this.isArabic() ? 'جاري العمل' : 'Pending');
  });

  getStatusClass(status: any): string {
    const id = status?.id;
    if (id == null) return ORDER_STATUS_DEFAULT_COLOR_CLASS;
    return ORDER_STATUS_ID_TO_COLOR_CLASS[Number(id)] ?? ORDER_STATUS_DEFAULT_COLOR_CLASS;
  }

  totalPeoplePrice = computed(() => {
    const items = this.visibleItems();

    return items.reduce((total: number, item: any) => {
      // product
      if (item.product) {
        return total + Number(item.product?.peoplePrice ?? 0) * Number(item.quantity ?? 0);
      }

      // Offer / Bundle
      const products = item.products ?? item.offer?.products ?? item.bxGyOffer?.products ?? item.bundle?.products ?? [];

      const itemTotal = products.reduce((sum: number, product: any) => {
        return sum + Number(product?.product?.peoplePrice ?? 0) * Number(product?.quantity ?? 0);
      }, 0);

      return total + itemTotal;
    }, 0);
  });

  statusStyle = computed(() => this.getStatusClass(this.orderData()?.status));

  successDeliverNotification = signal(false);

  cashReceived = signal<number | null>(null);
  showMismatchWarning = signal(false);

  ngOnInit() {
    const subscription = this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.store.dispatch(resetPackageActions());
        this.loadOrderData(id);
      }
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  private setOptimisticStatus(id: number) {
    const titles = ORDER_STATUS_TITLES[id];
    if (!titles) return;
    this.orderData.update((current) => {
      if (!current) return null;
      return {
        ...current,
        status: { ...current.status, id: String(id), titleEn: titles.titleEn, titleAr: titles.titleAr },
      };
    });
  }

  loadOrderData(id: string) {
    this.loading.set(true);
    this.error.set('');
    this.store.dispatch(resetPackageActions());

    const subscription = forkJoin({
      items: this.orderService.getOrderById(id),
      summary: this.orderService.getOrderSummaryById(id),
    })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: ({ items, summary }) => {
          const merged = { ...items, ...summary };
          this.orderData.set(merged);
          this.store.dispatch(hydratePackageItems({ data: merged }));
          this.preloadImages(merged);
        },
        error: (err) => {
          this.error.set(
            this.isArabic() ? err.error?.messageAr || 'حدث بعض الخطأ' : err.error?.messageEn || 'some error occured!',
          );
        },
      });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  getPackageDataFor(itemId: string) {
    const state = this.packageState();
    const direct = state[itemId];
    if (direct) return direct;

    const item = this.orderData()?.items?.find((i) => +i.id === +itemId) as any;
    if (!item) return { packageQuantity: null, notPackageRows: [] };

    const offer = item?.offer ?? item?.bxGyOffer;
    const bundle = item?.bundle;
    const innerRows = (item.products ?? offer?.products ?? bundle?.products ?? []) as any[];
    if (innerRows.length === 0) return { packageQuantity: null, notPackageRows: [] };

    const notPackageRows: any[] = [];
    let packagedQty = 0;
    innerRows.forEach((row: any) => {
      const inner = this.packageState()[`${item.id}_${row.id}`] ?? this.packageState()[row.id];
      if (inner) {
        packagedQty += inner.packageQuantity != null ? +inner.packageQuantity || 0 : 0;
        notPackageRows.push(...(inner.notPackageRows ?? []));
        return;
      }
      const { packaged, notPackagedRows } = this.packagingOfProduct(row);
      packagedQty += packaged?.quantity != null ? +packaged.quantity || 0 : 0;
      notPackageRows.push(...notPackagedRows);
    });

    return {
      packageQuantity: packagedQty > 0 ? String(packagedQty) : null,
      notPackageRows,
    };
  }

  private packagingOfItem(item: any): { packaged: any; notPackagedRows: any[] } {
    const offer = item?.offer ?? item?.bxGyOffer;
    const bundle = item?.bundle;
    const innerRows = (item?.products ?? offer?.products ?? bundle?.products ?? []) as any[];
    if (offer || bundle) {
      const rows: any[] = [];
      innerRows.forEach((row: any) => {
        rows.push(...this.packagingOfProduct(row).notPackagedRows);
      });
      return { packaged: null, notPackagedRows: rows };
    }
    return this.packagingOfProduct(item);
  }

  private packagingOfProduct(payload: any): { packaged: any; notPackagedRows: any[] } {
    const source = payload?.product ?? payload?.bundle;
    const history = payload?.packagingHistory ?? source?.packagingHistory;
    const hasHistory = history && (history.packaged !== undefined || history.notPackaged !== undefined);
    if (hasHistory) {
      return { packaged: history.packaged ?? null, notPackagedRows: history.notPackaged ?? [] };
    }
    return {
      packaged: payload?.package ?? source?.package ?? null,
      notPackagedRows: payload?.notPackage ?? source?.notPackage ?? [],
    };
  }

  private preloadImages(data: ViewOrderModel) {
    const fids = new Set<string>();
    for (const item of data.items) {
      if ((item as any).product?.profileImageFid) fids.add((item as any).product.profileImageFid);
      if ((item as any).offer?.profileImageFids?.length)
        (item as any).offer.profileImageFids.forEach((f: any) => fids.add(f));
      if ((item as any).bundle?.profileImageFid) fids.add((item as any).bundle.profileImageFid);
      if ((item as any).bundle?.imagesFids?.length) (item as any).bundle.imagesFids.forEach((f: any) => fids.add(f));
    }
    fids.forEach((fid) => this.resolveImage(fid));
  }

  loadRejectionReasons() {
    const subscription = this.reasonsService.getRejectionReasons().subscribe({
      next: (res: any) => {
        this.optionData.set(res.list ?? []);
      },
      error: (err) => {},
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  resolveImage(fid: string) {
    if (!fid || this.imagePreviews()[fid]) return;
    this.fileMgt.getImageUrl(fid).subscribe({
      next: (res) => {
        this.imagePreviews.update((map) => ({
          ...map,
          [fid]: res.isImage ? res.url : '/assets/product.png',
        }));
      },
      error: () => {
        this.imagePreviews.update((map) => ({
          ...map,
          [fid]: '/assets/product.png',
        }));
      },
    });
  }

  private showSuccess(msg: string) {
    this.successMessage.set(msg);
    this.successNotification.set(true);
    setTimeout(() => {
      this.successNotification.set(false);
      this.successMessage.set('');
    }, 3000);
  }

  onOpenPopup() {
    this.isPrintPopup.set(true);
  }
  onOpenPackage() {
    this.openPackage.set(true);
  }
  onOpenReviewalApprove() {
    this.reviewalApprovedPopup.set(true);
  }
  openShipPopup() {
    this.isShipPopup.set(true);
  }
  onOpenReject() {
    this.rejectReview.set(true);
    this.loadRejectionReasons();
  }
  onCloseReject() {
    this.rejectReview.set(false);
  }
  onOpenRepackage() {
    this.openRepackagePopup.set(true);
  }

  onClosePopup() {
    this.isPrintPopup.set(false);
  }
  onCloseRepackage() {
    this.openRepackagePopup.set(false);
  }
  onCloseReviewalApprove() {
    this.reviewalApprovedPopup.set(false);
  }
  onCloseShipPopup() {
    this.isShipPopup.set(false);
  }
  onCloseDeliver() {
    this.isDeliverPopup.set(false);
    this.errDeliver.set('');
    this.deliverAmountCollected.set(null);
  }
  onCloseUndeliver() {
    this.isUndeliverPopup.set(false);
    this.errUndeliver.set('');
  }
  onClosePartiallyDeliver() {
    this.isPartiallyDeliverPopup.set(false);
    this.errPartiallyDeliver.set('');
  }

  onPrint(comment: string) {
    this.printing.set(true);
    if (!this.orderId()) return;

    const subscription = this.actionsServie
      .printOrder(this.orderId()!, comment)
      .pipe(finalize(() => this.printing.set(false)))
      .subscribe({
        next: () => {
          this.setOptimisticStatus(ORDER_STATUS_ID.PRINTED);
          this.showSuccess(this.isArabic() ? 'تمت طباعة الطلب بنجاح.' : 'Order printed successfully.');
          this.onClosePopup();
        },
        error: (err) => {
          this.errPrintAction.set(
            this.isArabic() ? err.error?.messageAr || 'حدث بعض الخطأ' : err.error?.messageEn || 'some error occured!',
          );
        },
      });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onPackage() {
    this.setOptimisticStatus(ORDER_STATUS_ID.PACKAGED);
    this.openPackage.set(true);
  }

  onReviewalApproved(comment: string) {
    if (!this.orderId()) return;
    const subscription = this.actionsServie
      .confirmReview(this.orderId()!, comment)
      .pipe(finalize(() => this.repackaging.set(false)))
      .subscribe({
        next: () => {
          this.setOptimisticStatus(ORDER_STATUS_ID.REVIEW_APPROVED);
          this.reviewalApprovedPopup.set(false);
        },
        error: (err) => {
          this.errRepackageAction.set(
            this.isArabic() ? err.error?.messageAr || 'حدث بعض الخطأ' : err.error?.messageEn || 'some error occured!',
          );
        },
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onReject(data: { reasonId: number; otherReason: string; comment: string }) {
    const payload = {
      reason: {
        reasonId: data.reasonId,
        otherReason: data.otherReason,
      },
      comment: data.comment,
    };

    const sub = this.orderService.rejectOrder(this.orderId()!, payload).subscribe({
      next: () => {
        this.setOptimisticStatus(ORDER_STATUS_ID.REVIEW_REJECTED);
        this.rejectReview.set(false);
      },
      error: (error) => {
        console.error('Reject order failed:', error);
      },
    });
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  onShipOrder(comment: string) {
    this.shipping.set(true);
    if (!this.orderId()) return;
    const subscription = this.actionsServie
      .shipOrder(this.orderId()!, comment)
      .pipe(finalize(() => this.shipping.set(false)))
      .subscribe({
        next: () => {
          this.setOptimisticStatus(ORDER_STATUS_ID.SHIPPED);
          this.onCloseShipPopup();
        },
        error: (err: any) => {
          this.errorShip.set(
            this.isArabic() ? err.messageAr || 'حدث بعض الخطأ' : err.messageEn || 'some error occured!',
          );
        },
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  allItemsPackaged = computed(() => {
    const data = this.orderData();
    if (!data?.items?.length) return false;
    const state = this.packageState();
    return data.items.every((item) => {
      const offer = (item as any).offer ?? (item as any).bxGyOffer;
      const bundle = (item as any).bundle;
      if (offer || bundle) {
        const products = (item as any).products ?? bundle?.products ?? [];
        if (products.length === 0) return false;
        return products.every((p: any) => {
          const entry =
            state[`${item.id}_${(p as any).id}`] ??
            state[`${item.id}_${p.product?.id}`] ??
            state[p.product?.id] ??
            state[(p as any).id];
          return isRowProcessed(entry, p.quantity);
        });
      }
      return isRowProcessed(state[item.id], item.quantity);
    });
  });

  openPackagePopup(item: any) {
    const type: 'product' | 'offer' | 'bundle' = item.product ? 'product' : item.offer ? 'offer' : 'bundle';
    const source = item.product ?? item.offer ?? item.bundle;
    this.selectedPackageItem.set({
      id: item.id,
      titleEn: source?.titleEn ?? '',
      titleAr: source?.titleAr ?? '',
      quantity: item.quantity,
      type,
    });
    this.openPackage.set(true);
  }

  openProductPackagePopup(row: any, itemId?: string, type: 'offer' | 'bundle' = 'offer') {
    this.selectedPackageItem.set({
      id: row.id,
      titleEn: row.product?.titleEn ?? '',
      titleAr: row.product?.titleAr ?? '',
      quantity: row.quantity,
      type,
      storeKey: itemId ? `${itemId}_${row.id}` : `${row.id}`,
    });
    this.openPackage.set(true);
  }

  onSubmitted() {
    this.openPackage.set(false);
  }

  onOpenSubmitPackage() {
    this.submitPackagePopup.set(true);
  }
  onCloseSubmitPackage() {
    this.submitPackagePopup.set(false);
  }
  onOpenResetPackage() {
    this.resetPackagePopup.set(true);
  }
  onCloseResetPackage() {
    this.resetPackagePopup.set(false);
  }

  onResetPackage() {
    this.resettingPackage.set(true);
    this.errResetAction.set('');
    const id = this.orderId();
    if (!id) return;
    const subscription = this.packageService
      .resetPackage(id)
      .pipe(finalize(() => this.resettingPackage.set(false)))
      .subscribe({
        next: () => {
          this.resetPackagePopup.set(false);
          this.loadOrderData(id);
        },
        error: (err) => {
          this.errResetAction.set(
            this.isArabic() ? err?.error?.messageAr || 'حدث بعض الخطأ' : err?.error?.messageEn || 'some error occured!',
          );
        },
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onSubmitPackage(comment: string) {
    this.submitingPackage.set(true);
    this.errSubmitAction.set('');
    const id = this.orderId();
    if (!id) return;
    const subscription = this.packageService
      .submitPackage(id, comment)
      .pipe(finalize(() => this.submitingPackage.set(false)))
      .subscribe({
        next: () => {
          this.submitPackagePopup.set(false);
          this.loadOrderData(id);
        },
        error: (err) => {
          this.errSubmitAction.set(
            this.isArabic() ? err?.error?.messageAr || 'حدث بعض الخطأ' : err?.error?.messageEn || 'some error occured!',
          );
        },
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onReceiveReturnedProductsOrder(comment: string) {
    this.receivingReturnedProducts.set(true);
    const subscription = this.orderService
      .receiveReturnedProducts(this.orderId()!, comment)
      .pipe(finalize(() => this.receivingReturnedProducts.set(false)))
      .subscribe({
        next: () => {
          this.onCancelReceiveReturnedProducts();
        },
        error: (err: any) => {
          this.errReceivingReturnedProducts.set(
            this.isArabic() ? err.messageAr || 'حدث بعض الخطأ' : err.messageEn || 'some error occured!',
          );
        },
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onOpenConfirmPackage() {
    this.confirmPackagePopup.set(true);
  }
  onReceiveReturnedProductsPartially() {
    this.receiveReturnedProductsView.set(true);
  }
  onCancelReceiveReturnedProductsPartially() {
    this.receiveReturnedProductsView.set(false);
  }
  onReceiveReturnedProducts() {
    this.receiveReturnedProductsPopup.set(true);
  }
  onCancelReceiveReturnedProducts() {
    this.receiveReturnedProductsPopup.set(false);
  }
  onCloseConfirmPackage() {
    this.confirmPackagePopup.set(false);
  }

  buildConfirmPackagePayload(comment: string): ConfirmPackageRequestDto {
    const state = this.confirmPackageState();
    const items: ConfirmPackageItemDto[] = Object.entries(state).map(([itemId, entry]: [string, any]) => ({
      type: entry.type,
      itemId: +itemId,
      action: entry.action,
      ...(entry.products ? { products: entry.products } : {}),
    }));
    return { comment, items };
  }

  onConfirmPackage(comment: string) {
    this.confirmingPackage.set(true);
    this.errConfirmAction.set('');
    const id = this.orderId();
    if (!id) return;
    const payload = this.buildConfirmPackagePayload(comment);
    const subscription = this.packageService
      .confirmPackage(id, payload)
      .pipe(finalize(() => this.confirmingPackage.set(false)))
      .subscribe({
        next: (response: any) => {
          this.confirmPackagePopup.set(false);
          const returnedStatus = response?.status;
          if (returnedStatus) {
            this.orderData.update((currentData) => {
              if (!currentData) return null;
              return { ...currentData, status: returnedStatus };
            });
          }
        },
        error: (err) => {
          this.errConfirmAction.set(
            this.isArabic() ? err?.error?.messageAr || 'حدث بعض الخطأ' : err?.error?.messageEn || 'some error occured!',
          );
        },
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  private buildProductShortageData(item: any, type: 'product' | 'bundle'): ProductShortageData {
    const source = type === 'product' ? item.product : item.bundle;
    const { notPackagedRows } = this.packagingOfItem(item);
    const notPackageQty = notPackagedRows.reduce((acc: number, r: any) => acc + (r.quantity || 0), 0);
    const orderedQuantity = item.quantity;
    const availableQuantity = Math.max(0, +orderedQuantity - notPackageQty).toString();
    const packageData = this.getPackageDataFor(item.id);
    return {
      itemId: item.id,
      orderedQuantity,
      availableQuantity,
      remainingQuantity: availableQuantity,
      totalPharmacyPrice: item.totalPharmacyPrice,
      packageQuantity: packageData.packageQuantity,
      notPackageRows: packageData.notPackageRows ?? null,
      isPartiallyPackaged: notPackagedRows.length > 0,
      product: {
        id: source?.id,
        titleEn: source?.titleEn,
        titleAr: source?.titleAr,
        customerPrice: source?.customerPrice ?? source?.peoplePrice,
        businessPrice: source?.businessPrice ?? source?.storePrice,
        peoplePrice: source?.peoplePrice,
        storePrice: source?.storePrice,
        mfgDate: source?.mfgDate,
        expDate: source?.expDate,
        batchNumber: source?.batchNumber,
        profileImageFid: source?.profileImageFid,
      },
      ...(type === 'bundle'
        ? {
            bundle: {
              id: source?.id,
              titleAr: source?.titleAr,
              titleEn: source?.titleEn,
              products: source?.products,
              profileImageFid: source?.profileImageFid,
              totalBusinessPriceBeforeDiscount: source?.totalPharmacyPriceBeforeDiscount,
              discountType: source?.discountType,
              discountValue: source?.discountValue,
            },
          }
        : {}),
    };
  }

  openConfirmShortage(itemId: string, type: 'product' | 'bundle') {
    const data = this.orderData();
    const item = data?.items?.find((i) => +i.id === +itemId);
    if (!item) return;
    this.selectedItemData.set(this.buildProductShortageData(item, type));
    this.selectedItemType.set(type);
    this.isConfirmProductPopupOpen.set(true);
  }
  onCloseConfirmShortage() {
    this.isConfirmProductPopupOpen.set(false);
  }

  openDropFromOrder(itemId: string, type: 'product' | 'bundle') {
    this.selectedItemId.set(itemId);
    this.selectedItemType.set(type);
    this.isDropOrderPopupOpen.set(true);
  }
  onCloseDropFromOrder() {
    this.isDropOrderPopupOpen.set(false);
  }

  private buildOfferShortageData(item: any): ConvertShortageData {
    const offer = item.offer ?? item.bxGyOffer;
    const bundle = item.bundle;
    const innerRows = (item.products ?? offer?.products ?? bundle?.products ?? []) as any[];
    const products = innerRows.map((row: any) => {
      const { packaged, notPackagedRows } = this.packagingOfProduct(row);
      const packagedQty = packaged?.quantity != null ? +packaged.quantity || 0 : 0;
      const notPackageQty = notPackagedRows.reduce((acc: number, r: any) => acc + (+r.quantity || 0), 0);
      const availableQuantity = packagedQty > 0 ? packagedQty : Math.max(0, +row.quantity - notPackageQty);
      return {
        product: row.product,
        requiredQuantity: row.quantity,
        availableQuantity,
        packageQuantity: packagedQty > 0 ? String(packagedQty) : null,
        isAvailable: packagedQty > 0 || notPackageQty === 0,
      };
    });

    return {
      itemId: item.id,
      item,
      itemType: offer ? 'offer' : 'bundle',
      titleAr: offer?.titleAr ?? bundle?.titleAr,
      titleEn: offer?.titleEn ?? bundle?.titleEn,
      products,
    };
  }

  openConvertOffer(itemId: string) {
    const data = this.orderData();
    const item = data?.items?.find((i) => +i.id === +itemId);
    if (!item) return;

    this.selectedOfferData.set(this.buildOfferShortageData(item));
    this.isConvertProductsPopupOpen.set(true);
  }
  onCloseConvertOffer() {
    this.isConvertProductsPopupOpen.set(false);
  }

  openDropAllOffer(itemId: string) {
    const data = this.orderData();
    const item = data?.items?.find((i) => +i.id === +itemId);
    const offer = (item as any)?.offer ?? (item as any)?.bxGyOffer;
    this.selectedOfferItemId.set(itemId);
    this.selectedOfferItemType.set(offer ? 'offer' : 'bundle');
    this.isDropAllOfferPopupOpen.set(true);
  }
  onCloseDropAllOffer() {
    this.isDropAllOfferPopupOpen.set(false);
  }

  onDeliverOrder(comment: string, amountCollected?: number | null) {
    const id = this.orderId();
    if (!id) return;

    this.delivering.set(true);
    this.errDeliver.set('');

    const payload: { comment?: string; amountCollected?: number } = {
      comment: comment || '',
    };

    if (amountCollected !== null && amountCollected !== undefined && !Number.isNaN(Number(amountCollected))) {
      payload.amountCollected = Number(amountCollected);
    }

    const subscription = this.orderService
      .deliverOrder(id, payload as any)
      .pipe(finalize(() => this.delivering.set(false)))
      .subscribe({
        next: () => {
          this.setOptimisticStatus(ORDER_STATUS_ID.DELIVERED);
          this.onCloseDeliver();
          this.showSuccess(this.isArabic() ? 'تم تسليم الطلب بنجاح.' : 'Order delivered successfully.');
          this.loadOrderData(id);
        },
        error: (err) => {
          this.errDeliver.set(
            this.isArabic()
              ? err?.error?.messageAr || 'حدث بعض الخطأ'
              : err?.error?.messageEn || 'some error occurred!',
          );
        },
      });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onRepackage(comment: string) {
    this.repackaging.set(true);
    this.errRepackageAction.set('');

    const id = this.orderId();

    if (!id) {
      this.repackaging.set(false);
      return;
    }

    const subscription = this.orderService
      .repackageOrder(id, comment)
      .pipe(finalize(() => this.repackaging.set(false)))
      .subscribe({
        next: () => {
          this.setOptimisticStatus(ORDER_STATUS_ID.PACKAGED);
          this.openRepackagePopup.set(false);

          this.showSuccess(
            this.isArabic()
              ? 'تمت إعادة تعبئة الطلب بنجاح وإرساله للمراجعة.'
              : 'Order has been repackaged and sent for review successfully.',
          );
        },
        error: (err) => {
          this.errRepackageAction.set(
            this.isArabic() ? err?.error?.messageAr || 'حدث خطأ ما' : err?.error?.messageEn || 'Something went wrong!',
          );
        },
      });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onUndeliverOrder(comment: string) {
    const id = this.orderId();
    if (!id) return;
    this.undelivering.set(true);
    this.errUndeliver.set('');

    const subscription = this.orderService
      .undeliverOrder(id, {
        comment: '',
        reason: {
          reasonId: 1,
          otherReason: 'test reason',
        },
      })
      .pipe(finalize(() => this.undelivering.set(false)))
      .subscribe({
        next: () => {
          this.setOptimisticStatus(ORDER_STATUS_ID.UNDELIVERED);
          this.onCloseUndeliver();
          this.showSuccess(this.isArabic() ? 'تم إلغاء التسليم بنجاح.' : 'Order undelivered successfully.');
          this.loadOrderData(id);
        },
        error: (err) => {
          this.errUndeliver.set(
            this.isArabic()
              ? err?.error?.messageAr || 'حدث بعض الخطأ'
              : err?.error?.messageEn || 'some error occurred!',
          );
        },
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onPartiallyDeliverOrder(comment: string) {
    const id = this.orderId();
    if (!id) return;
    this.partiallyDelivering.set(true);
    this.errPartiallyDeliver.set('');

    const subscription = this.orderService
      .deliverOrder(id, comment)
      .pipe(finalize(() => this.partiallyDelivering.set(false)))
      .subscribe({
        next: () => {
          this.setOptimisticStatus(ORDER_STATUS_ID.PARTIALLY_DELIVERED);
          this.onClosePartiallyDeliver();
          this.showSuccess(this.isArabic() ? 'تم التسليم الجزئي بنجاح.' : 'Order partially delivered successfully.');
          this.loadOrderData(id);
        },
        error: (err) => {
          this.errPartiallyDeliver.set(
            this.isArabic()
              ? err?.error?.messageAr || 'حدث بعض الخطأ'
              : err?.error?.messageEn || 'some error occurred!',
          );
        },
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  loadUndeliveredReasons() {
    const subscription = this.reasonsService.getUndeliveredReasons().subscribe({
      next: (response) => {
        this.undeliveredReasons.set(response.list);
      },
      error: (error) => {
        this.errorShip.set(
          this.isArabic()
            ? error?.messageAr || 'حدث خطأ أثناء تحميل أسباب عدم التسليم'
            : error?.messageEn || 'Failed to load undelivered reasons',
        );
      },
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
  loadCancellationReasons() {
    const subscription = this.reasonsService.getCancellationReasons().subscribe({
      next: (response) => {
        this.cancellationReasons.set(response.list);
      },
      error: (error) => {
        this.cancelError.set(
          this.isArabic()
            ? error?.messageAr || 'حدث خطأ أثناء تحميل أسباب الإلغاء'
            : error?.messageEn || 'Failed to load cancellation reasons',
        );
      },
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
  onOpenCancel() {
    this.cancelError.set('');
    this.loadCancellationReasons();
    this.isCancelPopup.set(true);
  }
  onCloseCancel() {
    this.isCancelPopup.set(false);
    this.cancelError.set('');
  }

  canUndeliver = computed(() => {
    if (this.role() === 'admin') {
      return true;
    }
    const order = this.orderData();
    const currentUserId = this.authService.getUserId();

    if (!order?.distributor?.id || !currentUserId) {
      return false;
    }

    return Number(order.distributor.id) === Number(currentUserId);
  });
  handleActionClick(type?: 'deliver' | 'undeliver' | 'partially') {
    if (this.status() === 'pending' || this.status() === 'updated') {
      this.onOpenPopup();
    }
    if (this.status().toLowerCase() === 'packaged') {
      this.onOpenReviewalApprove();
    }
    if (this.status().toLowerCase() === 'approved') {
      this.openShipPopup();
    }
    if (this.status().toLowerCase() === 'rejected') {
      this.onOpenReviewalApprove();
    }
    if (this.status().toLowerCase() === 'reviewal-approved') {
      this.onOpenRepackage();
    }
    if (this.status().toLowerCase() === 'shipped') {
      if (type === 'deliver') {
        this.isDeliverPopup.set(true);
      }
      if (type === 'undeliver') {
        this.isUndeliverPopup.set(true);
        return;
      }
      if (type === 'partially') {
        this.isPartiallyDeliverPopup.set(true);
      }
    }
  }

  onOpenCollectCash() {
    this.isCollectCashPopup.set(true);
    this.errCollectCash.set('');
  }
  onCancelOrder(data: { reasonId: number; otherReason: string; comment: string }) {
    this.cancelling.set(true);
    this.cancelError.set('');
    const subscription = this.orderService
      .cancelOrder(this.orderId()!, {
        reason: {
          otherReason: data.otherReason,
          reasonId: data.reasonId,
        },
        comment: data.comment,
      })
      .pipe(
        finalize(() => {
          this.cancelling.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.onCloseCancel();
          this.setOptimisticStatus(ORDER_STATUS_ID.CANCELLED);
          this.cancelSuccess.set(true);

          setTimeout(() => {
            this.cancelSuccess.set(false);
          }, 3000);
        },
        error: () => {},
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
  onCloseCollectCash() {
    this.isCollectCashPopup.set(false);
    this.errCollectCash.set('');
    this.collectingCash.set(false);
  }

  onConfirmCollectCash(payload: { actualAmount: number; comment: string }) {
    const orderId = this.orderId();

    if (!orderId) {
      return;
    }

    this.collectingCash.set(true);

    const subscription = this.orderService
      .collectOrderCash(orderId, {
        amountCollected: payload.actualAmount,
        comment: payload.comment,
      })
      .pipe(
        finalize(() => {
          this.collectingCash.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.onCloseCollectCash();

          this.loadOrderData(orderId);
        },
        error: (error) => {
          this.errCollectCash.set(
            this.isArabic()
              ? error?.error?.messageAr || 'حدث خطأ ما'
              : error?.error?.messageEn || 'Something went wrong',
          );
        },
      });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
}
